import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { Alert, Button, Icons, Spinner, cx } from '../../components/ui.tsx';
import { BRAND, BrandIcon, FileTypeIcon } from '../../components/icons.tsx';
import { Tooltip } from '../../components/Tooltip.tsx';
import { formatBytes, formatCount, formatRelative, pluralise } from '../../lib/display.ts';
import { NewSheetButton } from '../sheets/NewSheetButton.tsx';
import { NewDocumentButton } from '../documents/NewDocumentButton.tsx';

/**
 * The landing page.
 *
 * Answers three questions in the order they get asked: what is in here, what
 * needs attention, and what was I last doing. Quick actions sit at the top
 * because starting work is the most common reason to be on this screen at all.
 */

interface DashboardStats {
  counts: {
    folders: number;
    files: number;
    spreadsheets: number;
    sheetTabs: number;
    documents: number;
  };
  storage: { onDisk: string; logical: string; saved: string; sheets: string };
  scanning: { pending: number; infected: number; failed: number };
  recent: Array<{
    kind: 'spreadsheet' | 'document' | 'file';
    id: string;
    title: string;
    subtitle?: string;
    href: string;
    at: string;
  }>;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardStats>('/api/dashboard'),
    staleTime: 30_000,
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-body-sm text-muted">
        <Spinner /> Loading…
      </div>
    );
  }

  if (query.isError || !query.data) return <Alert>Could not load the dashboard.</Alert>;

  const { counts, storage, scanning, recent } = query.data;
  const totalStorage = Number(storage.onDisk) + Number(storage.sheets);

  return (
    <div className="h-full overflow-y-auto">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div>
          <h1 className="text-h1 text-ink">Dashboard</h1>
          <p className="mt-0.5 text-meta text-muted">
            {pluralise(counts.spreadsheets, 'sheet')} · {pluralise(counts.documents, 'document')} ·{' '}
            {pluralise(counts.files, 'file')} · {formatBytes(totalStorage)} stored
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <NewSheetButton folderId={null} onError={() => undefined} />
          <NewDocumentButton folderId={null} />
          <Button variant="primary" icon={<Icons.Folder />} onClick={() => navigate('/drive')}>
            Open Drive
          </Button>
        </div>
      </header>

      {/* Things that want attention, and only when there are any. A row of
          zeroes is noise that trains the user to ignore the whole strip. */}
      {scanning.infected > 0 || scanning.failed > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {scanning.infected > 0 ? (
            <Flag tone="danger" label={`${scanning.infected} infected upload blocked`} />
          ) : null}
          {scanning.failed > 0 ? (
            <Flag tone="warning" label={`${scanning.failed} upload failed to scan`} />
          ) : null}
        </div>
      ) : null}

      {/* Five cards, one row: they are one sentence about the system, and
          a fifth card alone on a second line reads as an afterthought. */}
      <section className="mt-4 grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        <Stat
          label="Sheets"
          value={formatCount(counts.spreadsheets)}
          detail={`${pluralise(counts.sheetTabs, 'tab')} in total`}
          to="/drive"
        />
        <Stat
          label="Documents"
          value={formatCount(counts.documents)}
          detail="letters, letterheads and notes"
          to="/drive"
        />
        <Stat
          label="Files"
          value={formatCount(counts.files)}
          detail={`across ${pluralise(counts.folders, 'folder')}`}
          to="/drive"
        />
        <Stat
          label="Storage"
          value={formatBytes(totalStorage)}
          detail={`${formatBytes(Number(storage.saved))} saved by deduplication`}
          to="/drive"
        />
        <Stat
          label="Folders"
          value={formatCount(counts.folders)}
          detail="in the Drive"
          to="/drive"
        />
      </section>

      <section className="mt-4 grid gap-4 lg:grid-cols-[1fr,340px]">
        <div className="rounded-lg border border-border-strong bg-surface">
          <h2 className="border-b border-border px-4 py-2.5 text-h3 text-ink">Recent</h2>
          {recent.length === 0 ? (
            <p className="px-4 py-8 text-center text-body-sm text-muted">
              Nothing yet. Start a sheet and it will appear here.
            </p>
          ) : (
            <ul>
              {recent.map((item) => (
                <li key={`${item.kind}-${item.id}`}>
                  <Link
                    to={item.href}
                    className="flex items-center gap-3 border-b border-border px-4 py-2 transition-fast last:border-0 hover:bg-blue-50"
                  >
                    <span className="shrink-0" aria-hidden>
                      {item.kind === 'spreadsheet' ? (
                        <BrandIcon src={BRAND.excel} size={20} />
                      ) : item.kind === 'document' ? (
                        <BrandIcon src={BRAND.word} size={20} />
                      ) : (
                        <FileTypeIcon name={item.title} size={20} />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body-sm text-ink">{item.title}</span>
                      {item.subtitle ? (
                        <span className="block truncate text-micro text-muted">
                          {item.subtitle}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-micro text-subtle">
                      {formatRelative(item.at)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-border-strong bg-surface p-4">
            <h2 className="text-h3 text-ink">Storage</h2>

            <dl className="mt-3 space-y-2">
              <Line label="Uploaded files" value={formatBytes(storage.onDisk)} />
              <Line label="Sheets" value={formatBytes(storage.sheets)} />
              <Line label="Total" value={formatBytes(totalStorage)} emphasis />
            </dl>

            {Number(storage.saved) > 0 ? (
              <Tooltip
                label="Deduplication"
                detail="The same file filed in two places is stored once. This is what that saves."
              >
                <p className="mt-3 cursor-help rounded-md bg-success-bg px-3 py-2 text-micro text-[var(--success)]">
                  {formatBytes(storage.saved)} saved by storing duplicate files once.
                </p>
              </Tooltip>
            ) : null}

            {scanning.pending > 0 ? (
              <p className="mt-2 text-micro text-muted">
                {pluralise(scanning.pending, 'upload')} still being scanned.
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  detail,
  to,
}: {
  label: string;
  value: string;
  detail: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-border-strong bg-surface p-4 transition-fast hover:border-[var(--blue-400)] hover:bg-blue-50"
    >
      <span className="block text-meta text-muted">{label}</span>
      <span className="mt-1 block text-h1 text-ink" data-numeric>
        {value}
      </span>
      <span className="mt-0.5 block text-micro text-muted">{detail}</span>
    </Link>
  );
}

function Line({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div
      className={cx(
        'flex justify-between gap-3',
        emphasis && 'border-t border-border pt-2 font-semibold text-ink',
      )}
    >
      <dt className={cx('text-body-sm', emphasis ? 'text-ink' : 'text-muted')}>{label}</dt>
      <dd className="text-body-sm" data-numeric>
        {value}
      </dd>
    </div>
  );
}

function Flag({ tone, label, to }: { tone: 'danger' | 'warning'; label: string; to?: string }) {
  const className = cx(
    'rounded-full px-3 py-1 text-meta font-medium',
    tone === 'danger' ? 'bg-danger-bg text-[var(--danger)]' : 'bg-warning-bg text-warning',
  );

  return to ? (
    <Link to={to} className={cx(className, 'hover:underline')}>
      {label}
    </Link>
  ) : (
    <span className={className}>{label}</span>
  );
}
